import type { LocalizedText } from "./types";

/**
 * Default fallback order used when a requested locale has no translation.
 * `ru` first because existing medical content is authored in Russian.
 */
export const LOCALE_FALLBACK_ORDER = ["ru", "en", "kz"] as const;

/**
 * Resolve a {@link LocalizedText} value (a plain string OR a `{ <lang>: string }`
 * map) to a single string for the requested `locale`.
 *
 * Backward-compatible:
 * - plain string  → returned as-is (legacy single-language fields);
 * - localized map → `map[locale]`, then the fallback order, then the first
 *   non-empty value present. Missing/empty → "".
 */
export function resolveLocalized(
  value: LocalizedText | null | undefined,
  locale = "ru",
): string {
  if (value == null) return "";
  if (typeof value === "string") return value;

  const pick = (lang: string): string | undefined => {
    const v = value[lang];
    return typeof v === "string" && v.trim().length > 0 ? v : undefined;
  };

  const direct = pick(locale);
  if (direct !== undefined) return direct;

  for (const lang of LOCALE_FALLBACK_ORDER) {
    const v = pick(lang);
    if (v !== undefined) return v;
  }

  const first = Object.values(value).find(
    (v) => typeof v === "string" && v.trim().length > 0,
  );
  return first ?? "";
}

/**
 * Resolve an array of localized options to plain strings for `locale`.
 * Empty results are dropped so select inputs never render blank choices.
 */
export function resolveOptions(
  options: readonly LocalizedText[] | null | undefined,
  locale = "ru",
): string[] {
  if (!options) return [];
  return options.map((o) => resolveLocalized(o, locale)).filter((o) => o.length > 0);
}
